// src/admin/pages/sold-vehicles/SoldVehicleTable.tsx
import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Print as PrintIcon } from '@mui/icons-material';
import { Product } from '../../../enums/product';

interface SoldVehicleTableProps {
  cars: Product[];
  onEdit: (car: Product) => void;
  onPrintReceipt: (car: Product) => void;
  isDark?: boolean;
  canEdit?: boolean;
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string };
  const lightPalette: Record<string, PaletteEntry> = {
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242' },
  };
  const darkPalette: Record<string, PaletteEntry> = {
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0' },
  };
  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;
  return {
    backgroundColor: p.bg, borderColor: p.border, color: p.text, fontWeight: 600,
    border: `1px solid ${p.border}`, '& .MuiChip-label': { color: p.text },
  };
};

const SoldVehicleTable: React.FC<SoldVehicleTableProps> = ({ cars, onEdit, onPrintReceipt, isDark = false, canEdit = false }) => {
  const formatDate = (d: any) => {
    if (!d) return '-';
    const date = d instanceof Date ? d : d?.toDate?.() || new Date(d);
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
  };

  return (
    <TableContainer component={Paper} sx={{ height: '100%', '& .MuiTable-root': { minWidth: 950 } }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Car</TableCell>
            <TableCell>Origin</TableCell>
            <TableCell>Buyer</TableCell>
            <TableCell>Final Price</TableCell>
            <TableCell>Payment</TableCell>
            <TableCell>Registration</TableCell>
            <TableCell>Sold By</TableCell>
            <TableCell>Sold Date</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cars.map((c) => (
            <TableRow key={c.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' }, '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar variant="rounded" src={c.images?.[0]} sx={{ width: 36, height: 36 }}>
                    {c.name?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, maxWidth: 200 }}>
                      {c.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {c.sku}
                    </Typography>
                  </Box>
                </Stack>
              </TableCell>
              <TableCell>
                <Chip
                  label={c.isImported ? 'Imported' : 'Pre-Owned / Local'}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx(c.isImported ? 'info' : 'success', isDark)}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.buyerName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">{c.buyerPhone}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{c.finalSalePrice ? c.finalSalePrice.toLocaleString() : '—'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">{c.paymentMethod || '—'}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={c.registrationTransferStatus || 'Not Started'}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx(c.registrationTransferStatus === 'Completed' ? 'success' : 'info', isDark)}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">{c.soldByName || '—'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">{formatDate(c.soldAt)}</Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Print Sale Receipt">
                  <IconButton onClick={() => onPrintReceipt(c)} size="small">
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip title="Edit Sale Details">
                    <IconButton onClick={() => onEdit(c)} size="small" color="warning">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SoldVehicleTable;
